import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ViewListIcon from '@mui/icons-material/ViewList';

const ExpensesPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    expenses_page: false,
    accounts: false,
    categories: false
  });
  
  // Expenses sections
  const sections = [
    { 
      key: 'expenses_page', 
      label: 'Expenses Page', 
      permissionPrefix: 'expenses.expenses',
      icon: <ReceiptIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
      hasApprove: true
    },
    { 
      key: 'accounts', 
      label: 'Accounts Page', 
      permissionPrefix: 'expenses.accounts',
      icon: <AccountBalanceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
      hasApprove: false
    },
    { 
      key: 'categories', 
      label: 'Categories Page', 
      permissionPrefix: 'expenses.categories',
      icon: <ViewListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
      hasApprove: false
    }
  ];

  const renderSection = (section) => {
    const isExpanded = expandedSections[section.key] !== false;
    
    // Find permissions for this section
    const sectionPermissions = permissions.filter(p => 
      p.name && p.name.includes(section.permissionPrefix)
    );

    if (sectionPermissions.length === 0) return null;

    // Group permissions by action type
    const viewPerm = sectionPermissions.find(p => p.name.includes('.view'));
    const editPerm = sectionPermissions.find(p => p.name.includes('.edit'));
    const deletePerm = sectionPermissions.find(p => p.name.includes('.delete'));
    const approvePerm = sectionPermissions.find(p => p.name.includes('.approve'));

    return (
      <Box 
        key={section.key}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Section Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [section.key]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {section.icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {section.label}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {section.label.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {section.label.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission */}
            {deletePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: section.hasApprove ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {section.label.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve Permission (only for Expenses Page) */}
            {section.hasApprove && approvePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve expense requests
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePerm.id]) {
                      newPermissions[approvePerm.id] = {};
                    }
                    newPermissions[approvePerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {sections.map(section => renderSection(section))}
    </Box>
  );
};

export default ExpensesPermissions;
